/**
 * Alert Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * For displaying important messages to users
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface AlertProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * Alert variant
   * @default 'default'
   */
  variant?: 'default' | 'info' | 'success' | 'warning' | 'error' | 'secondary' | 'destructive';

  /**
   * Icon to display (optional)
   */
  icon?: React.ReactNode;

  /**
   * Show close button
   * @default false
   */
  dismissible?: boolean;

  /**
   * Callback when alert is dismissed
   */
  onDismiss?: () => void;
}

// ============================================================================
// Alert Root
// ============================================================================

/**
 * Alert Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (4.5:1 contrast ratios)
 * - Semantic colors for different message types
 * - Optional icon and dismiss button
 * - ARIA role for screen readers
 * - Dark/Light mode support
 *
 * @example
 * ```tsx
 * // Success message
 * <Alert variant="success">
 *   <AlertTitle>Erfolgreich gespeichert</AlertTitle>
 *   <AlertDescription>
 *     Ihr Produkt wurde erfolgreich erstellt.
 *   </AlertDescription>
 * </Alert>
 *
 * // Error with icon
 * <Alert variant="error" icon={<AlertCircle />}>
 *   <AlertTitle>Fehler</AlertTitle>
 *   <AlertDescription>
 *     Das Produkt konnte nicht gespeichert werden.
 *   </AlertDescription>
 * </Alert>
 *
 * // Dismissible
 * <Alert variant="info" dismissible onDismiss={() => console.log('Dismissed')}>
 *   <AlertDescription>Neue Version verfügbar!</AlertDescription>
 * </Alert>
 * ```
 */
export const Alert = forwardRef<ElementRef<'div'>, AlertProps>(
  (
    {
      variant = 'default',
      icon,
      dismissible = false,
      onDismiss,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Determine ARIA role based on variant
    const role = variant === 'error' ? 'alert' : variant === 'warning' ? 'alert' : 'status';

    return (
      <div
        ref={ref}
        role={role}
        aria-live={variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'}
        className={cn(
          'relative w-full rounded-lg border p-4',
          'flex gap-3',

          // Variant styles (WCAG AA: 4.5:1 contrast)
          {
            // Default (Neutral)
            'bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-100': variant === 'default',

            // Info (kein Blau lt. Briefing -> Orange-Marke)
            'bg-primary-50 dark:bg-primary-900/30 border-primary-200 dark:border-primary-800 text-primary-900 dark:text-primary-100': variant === 'info',

            // Success (Green)
            'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100': variant === 'success',

            // Warning (Amber)
            'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100': variant === 'warning',

            // Error / Destructive (Red)
            'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100': variant === 'error' || variant === 'destructive',

            // Secondary (Neutral Dark, Briefing v1.0)
            'bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100': variant === 'secondary',
          },

          className
        )}
        {...props}
      >
        {/* Icon */}
        {icon && (
          <div className="shrink-0 pt-0.5" aria-hidden="true">
            {icon}
          </div>
        )}

        {/* Content */}
        <div className="flex-1">{children}</div>

        {/* Dismiss Button */}
        {dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              'shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              // BFSG: Touch target
              'min-h-[44px] min-w-[44px]',
              'inline-flex items-center justify-center'
            )}
            aria-label={t('common.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';

// ============================================================================
// Alert Title
// ============================================================================

export type AlertTitleProps = ComponentPropsWithoutRef<'h5'>;

export const AlertTitle = forwardRef<ElementRef<'h5'>, AlertTitleProps>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
);

AlertTitle.displayName = 'AlertTitle';

// ============================================================================
// Alert Description
// ============================================================================

export type AlertDescriptionProps = ComponentPropsWithoutRef<'div'>;

export const AlertDescription = forwardRef<ElementRef<'div'>, AlertDescriptionProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-sm leading-relaxed [&_p]:leading-relaxed', className)}
      {...props}
    />
  )
);

AlertDescription.displayName = 'AlertDescription';
